"use client";

import { type NowPlaying, type UpNextTrack } from "@/lib/api";
import { useElapsedSeconds } from "@/lib/use-elapsed-seconds";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlbumArt } from "@/components/dashboard/album-art";

// Home dashboard's "Up Next" card — a read-only preview of what the
// active playlist will play next (GET /playout/up-next, which never
// mutates anything — see that route's docstring). Column layout per the
// approved design: # / album art / estimated play time / title+artist /
// duration.
//
// "Time" is an ESTIMATE, stated as such, not a promise: it's the current
// track's real remaining time (duration - elapsed, from the same
// started_at the Now Playing player uses) plus each upcoming track's own
// duration stacked after it. It doesn't account for the 3s crossfade
// overlap between tracks or any processing/network jitter, and whatever
// is "up next" can genuinely change before it plays (a playlist gets
// activated, a new track finishes processing, etc.) — see
// app/api/routes/playout.py's get_up_next docstring.

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatClockTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// The card always shows this many rows. When fewer tracks remain (the
// playlist is nearly finished, or nothing is queued) the empty rows stay as
// greyed-out placeholders so the card keeps its height and the cards in its
// row don't change size.
const ROW_COUNT = 5;

export function UpNextCard({
  nowPlaying,
  upNext,
}: {
  nowPlaying: NowPlaying | null;
  upNext: UpNextTrack[];
}) {
  const elapsed = useElapsedSeconds(nowPlaying?.started_at);

  // Walk forward from "now": first slot starts when the current track's
  // real remaining time runs out (or immediately, if nothing's reported
  // as playing), then each subsequent slot starts after the previous
  // track's own duration.
  let cursorMs = Date.now();
  if (nowPlaying?.duration_seconds) {
    const remaining = Math.max(0, nowPlaying.duration_seconds - elapsed);
    cursorMs += remaining * 1000;
  }
  const rows = upNext.map((track) => {
    // Greyed tracks have already started — they take no future time.
    if (track.greyed) return { track, playAt: null as Date | null };
    const playAt = new Date(cursorMs);
    cursorMs += (track.duration_seconds ?? 0) * 1000;
    return { track, playAt };
  });
  const showingLastTracks = rows.some((r) => r.track.greyed);

  return (
    <Card>
      <CardHeader className="justify-between">
        <CardTitle>Up Next</CardTitle>
        <Badge variant="blue">
          {showingLastTracks ? "Last tracks · nothing queued after" : "Auto DJ"}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col divide-y divide-border-light">
          {Array.from({ length: ROW_COUNT }, (_, i) => {
            const row = rows[i];
            if (row) {
              const { track, playAt } = row;
              return (
                <div
                  key={`${track.id}-${i}`}
                  className={`flex items-center gap-3 py-2.5 first:pt-0 last:pb-0 ${
                    track.greyed ? "opacity-40" : ""
                  }`}
                >
                  <div className="w-4 shrink-0 text-xs font-semibold text-text-tertiary">{i + 1}.</div>
                  <AlbumArt trackId={track.id} hasCoverArt={track.has_cover_art} size="sm" />
                  <div className="w-16 shrink-0 text-xs text-text-tertiary">{playAt ? formatClockTime(playAt) : "Played"}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{track.title}</span>
                      {track.requested && (
                        <span className="shrink-0 rounded-full bg-purple-100 px-1.5 py-px text-[10px] font-semibold text-accent-purple">
                          Request
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-text-tertiary">{track.artist}</div>
                  </div>
                  <div className="shrink-0 text-xs text-text-tertiary">
                    {formatDuration(track.duration_seconds)}
                  </div>
                </div>
              );
            }
            const label =
              i === rows.length
                ? rows.length === 0
                  ? "Nothing queued"
                  : "End of playlist"
                : "";
            return (
              <div
                key={`empty-${i}`}
                aria-hidden={label ? undefined : true}
                className="flex items-center gap-3 py-2.5 opacity-40 first:pt-0 last:pb-0"
              >
                <div className="w-4 shrink-0 text-xs font-semibold text-text-tertiary">{i + 1}.</div>
                <div className="h-10 w-10 shrink-0 rounded-md bg-bg-main" />
                <div className="w-16 shrink-0 text-xs text-text-tertiary">—</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-tertiary">{label || "—"}</div>
                  <div className="truncate text-xs text-text-tertiary">
                    {label && rows.length === 0 ? "No active playlist, or nothing ready" : "—"}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-text-tertiary">—</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
